import { useState, useEffect } from 'react';
import { fetchAPI } from '../utils/apiCore';

/**
 * Custom hook to fetch dropdown options from the backend.
 * 
 * @param {string} endpoint - The specific options endpoint (e.g., 'studies', 'kit-types', 'enums/ConsentInactiveReason')
 * @returns {object} { options: [{ value, label }], loading, error }
 */
const useOptions = (endpoint) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const getOptions = async () => {
      try {
        setLoading(true);
        // We use fetchAPI directly. Ensure it's imported correctly.
        const response = await fetchAPI(`biobank/options/${endpoint}`, 'GET');
        
        if (isMounted) {
          // Response should be an array of { value, label }
          if (Array.isArray(response)) {
            setOptions(response);
          } else if (response && Array.isArray(response.content)) {
             setOptions(response.content);
          } else {
             setOptions([]);
          }
          setError(null);
        }
      } catch (err) {
        console.error(`Error fetching options for ${endpoint}:`, err);
        if (isMounted) {
          setError(err);
          setOptions([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (endpoint) {
      getOptions();
    } else {
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [endpoint]);

  return { options, loading, error };
};

export default useOptions;
